import React, { useState } from 'react';
import { ISourceStore } from '@stores/source';
import classes from '../sourceSetup.module.scss';
import { Body, H3 } from '@ui-library/typography';
import message from 'antd/lib/message';
import { Flex } from '@ui-library/misc';
import Button from '@ui-library/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import WriteKeyContainer from '../writeKey';
import InvitTeamMembers from '../inviteTeamMembers';
import useStores from '@stores/useStores';
import { CodeSnippet, Description, Link, LineBreaker, InfoMessage, InfoContainer } from '../styles';
import { ICodeSnippet } from '../constants/eventStreams';

export interface ISetupProps {
  source: ISourceStore;
  goToGuide: () => void;
  goToTeammates: () => void;
}
interface ISdkSetupProps extends ISetupProps {
  codeSnippets: ICodeSnippet[];
}

const getUpdatedCodeSnippet = (content: string, key: string, dataPlaneURL: string) => {
  const urlLabel = 'DATA_PLANE_URL';
  const keyLabel = 'WRITE_KEY';
  const updateKey = `"${key}"`;
  const udpatedUrl = `"${dataPlaneURL}"`;
  const updatedContent = content.replace(keyLabel, updateKey);
  return !dataPlaneURL ? updatedContent : updatedContent.replace(urlLabel, udpatedUrl);
};

const SdkSetup = ({ source, goToGuide, codeSnippets, goToTeammates }: ISdkSetupProps) => {
  const [activeCodeIndex, setActiveCodeIndex] = useState(0);
  const { workspaceStore } = useStores();
  const sdkName = source?.sourceDef?.displayName;
  const content = getUpdatedCodeSnippet(
    codeSnippets[activeCodeIndex].code,
    source.writeKey,
    workspaceStore.dataPlane,
  );
  const handleCopySnippet = () => {
    navigator.clipboard.writeText(content || '');
    message.info(`Code snippet copied!`);
  };

  return (
    <div className={classes.sdkSource}>
      <H3>Install RudderStack {sdkName} SDK</H3>
      {codeSnippets.length === 1 ? (
        <Description>
          To install the SDK, place the code snippet below in the
          {' <head>'} section of your website.
          <Link onClick={goToGuide}> Learn more</Link>
        </Description>
      ) : (
        <Description>
          To install the SDK, add the following code to the
          <span className={classes.highlight}> onCreate</span> method in your{' '}
          <span className={classes.highlight}> Application</span> class.
          <Link onClick={goToGuide}> Learn more</Link>
        </Description>
      )}
      <Flex className={classes.tabsContainer}>
        {codeSnippets.length > 1 &&
          codeSnippets.map((codeSnippet, index) => (
            <Button
              key={codeSnippet.language}
              className={classes.button}
              onClick={() => setActiveCodeIndex(index)}
              secondary={index !== activeCodeIndex}
            >
              {codeSnippet?.language}
            </Button>
          ))}
      </Flex>
      <CodeSnippet data-testid={`code-snippet-${activeCodeIndex}`}>
        <code>{content}</code>
      </CodeSnippet>
      <Flex className={classes.copyContainer} alignItems="center">
        <Button onClick={handleCopySnippet} className={classes.copyButton}>
          <Flex>
            <FontAwesomeIcon className={classes.icon} icon={regular('copy')} />
            <Body className={classes.copyText}>Copy snippet</Body>
          </Flex>
        </Button>
        <InvitTeamMembers goToTeammates={goToTeammates} />
      </Flex>
      <LineBreaker />
      <InfoContainer>
        <InfoMessage>
          <FontAwesomeIcon className={classes.icon} icon={solid('circle-info')} />
          The write key is already in the code snippet. This is for reference only.
        </InfoMessage>
      </InfoContainer>
      <WriteKeyContainer writeKey={source.writeKey} />
    </div>
  );
};

export default SdkSetup;
